import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { API_ENDPOINTS } from './apiConfig';
import { getStatusMessage } from './statusMessages';
import { useAuth } from './AuthContext';
import axios from 'axios';

function ForgotPassword() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const validateEmail = (value) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return 'Email is required.';
    if (trimmed.length > 100) return 'Email address is too long.';
    if (/[\x00-\x1F\x7F\\"]/g.test(trimmed)) return 'Email contains invalid characters.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) return 'Please enter a valid email address.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setMessage('');
    setLoading(true);

    const emailError = validateEmail(email);
    if (emailError) {
      setErrors([emailError]);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(
        API_ENDPOINTS.FORGOTPSWD,
        { email: email.trim().toLowerCase() },
        { headers: { token } }
      );

      const { result, user, opt, errors: serverErrors } = res.data;

      if (result === 1) {
        setMessage(getStatusMessage(opt));
        setTimeout(() => {
          navigate('/ChangePassword', { state: { email, rotp: opt } });
        }, 1500);
      } else {
        const allMessages = [];
        if (Array.isArray(serverErrors)) allMessages.push(...serverErrors);
        if (user !== undefined) allMessages.push(getStatusMessage(user));
        if (opt !== undefined) allMessages.push(getStatusMessage(opt));
        setErrors(allMessages.length ? allMessages : ['Enter a valid email.']);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.result === 0 && data?.user === 0) {
        setErrors(['You are not registered. Please sign up.']);
      } else {
        setErrors(['Server error. Please try again later.']);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Forgot Password | LuvMarry - Reset Your Account</title>
        <meta
          name="description"
          content="Forgot your LuvMarry password? Enter your email address to receive an OTP and reset your account securely."
        />
        <meta
          name="keywords"
          content="forgot password LuvMarry, OTP reset Indian matrimony"
        />
        <link rel="canonical" href="https://luvmarry.com/ForgotPassword" />
      </Helmet>

      <div
        className="d-flex flex-column justify-content-start align-items-center min-vh-100"
        style={{
          background: 'linear-gradient(135deg, #ffe0f0, #d0e8ff)',
          paddingTop: '100px',
          paddingBottom: '40px',
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          }}
        >
          <h1 className="text-center fw-bold mb-4" style={{ color: '#ff69b4', fontSize: '1.5rem' }}>
            <i className="bi bi-key-fill me-2"></i>Forgot Password
          </h1>

          <form onSubmit={handleSubmit} aria-label="Forgot Password Form">
            <div className="mb-3">
              <label className="form-label text-muted" htmlFor="email">
                Enter your Email
              </label>
              <input
                id="email"
                type="email"
                className="form-control"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <button
              type="submit"
              className="btn w-100 d-flex justify-content-center align-items-center gap-2 py-2 rounded-pill shadow-sm"
              style={{ backgroundColor: '#ff69b4', color: 'white', fontWeight: 500 }}
              disabled={loading}
            >
              <i className="bi bi-envelope-fill"></i>
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>

          {message && (
            <div className="alert alert-success mt-3 text-center" style={{ whiteSpace: 'pre-line' }}>
              {message}
            </div>
          )}

          {errors.length > 0 && (
            <div
              className="alert alert-danger mt-3"
              style={{ backgroundColor: '#ffe0e0', color: '#cc0000' }}
            >
              <ul className="mb-0">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-center mt-4">
            <small className="text-muted">
              Don’t have an account?{' '}
              <a href="/signup" className="text-primary">
                Sign up
              </a>
            </small>
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
